import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { FacebookLogo, LinkedinLogo, TiktokLogo, TwitterLogo, GoogleLogo, Buildings, Receipt, Microphone, VideoCamera, Lock, CheckCircle, Plug, ArrowRight, X, Lightning } from "@phosphor-icons/react";
import { PageHeader } from "../components/ui/kaizen";

const PROVIDER_META = {
  meta: { label: "Meta (Facebook + Instagram)", icon: FacebookLogo, color: "#1877F2", category: "social", instructions: "Crée une App sur developers.facebook.com, ajoute Facebook Login + Instagram Graph API, demande les permissions pages_manage_posts + instagram_content_publish." },
  linkedin: { label: "LinkedIn (Page entreprise)", icon: LinkedinLogo, color: "#0A66C2", category: "social", instructions: "Crée une App sur developer.linkedin.com avec les produits Sign In + Share on LinkedIn + Marketing Developer Platform." },
  tiktok: { label: "TikTok for Business", icon: TiktokLogo, color: "#0A0A0A", category: "social", instructions: "Crée une App sur developers.tiktok.com avec le produit Content Posting API." },
  twitter: { label: "X / Twitter", icon: TwitterLogo, color: "#0A0A0A", category: "social", instructions: "Plan Basic requis (~100 USD/mois) sur developer.x.com avec OAuth 2.0 Read+Write." },
  gmail: { label: "Gmail", icon: GoogleLogo, color: "#EA4335", category: "courriels", instructions: "Crée un projet Google Cloud, active Gmail API, configure l'écran OAuth (External / Test)." },
  quickbooks: { label: "QuickBooks Online", icon: Buildings, color: "#2CA01C", category: "comptabilite", instructions: "Crée une App sur developer.intuit.com avec scope Accounting (sandbox d'abord)." },
  wellybox: { label: "WellyBox", icon: Receipt, color: "#FF6B35", category: "comptabilite", instructions: "Récupère ta clé API dans Settings → API depuis ton dashboard WellyBox." },
  elevenlabs: { label: "ElevenLabs (voix)", icon: Microphone, color: "#5436DA", category: "media", instructions: "Plan Creator (22$/mois) requis. Récupère ta clé API dans Profile → API Key." },
  heygen: { label: "HeyGen (avatar vidéo)", icon: VideoCamera, color: "#5E72EB", category: "media", instructions: "Plan Creator (29$/mois) requis. Récupère ta clé API dans Settings → API." },
};

const CATEGORIES = [
  { k: "social", label: "Réseaux sociaux", desc: "Publication automatique multi-plateformes" },
  { k: "courriels", label: "Courriels", desc: "Lecture, classement et réponses assistées" },
  { k: "comptabilite", label: "Comptabilité", desc: "Synchronisation TPS/TVQ et reçus" },
  { k: "media", label: "Voix & Vidéo", desc: "Clonage vocal + génération vidéo avec avatar" },
];

export default function Integrations() {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/integrations").then(r => setList(r.data)).catch(() => {});

  useEffect(() => {
    load();
    // Détecte le retour OAuth Gmail via query param (?gmail=connected ou ?gmail=error)
    const params = new URLSearchParams(window.location.search);
    if (params.get("gmail") === "connected") {
      toast.success("Compte Gmail connecté avec succès");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("gmail") === "error") {
      toast.error(`Erreur de connexion Gmail: ${params.get("reason") || "inconnue"}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const connect = async (provider) => {
    // TikTok possède une page dédiée (OAuth PKCE + bon redirect_uri /api/tiktok/oauth/callback).
    // On y redirige pour éviter le flux générique (qui utilise /api/integrations/tiktok/callback non autorisé).
    if (provider === "tiktok") {
      window.location.href = "/app/tiktok";
      return;
    }
    setBusy(provider);
    try {
      const { data } = await api.get(`/integrations/${provider}/auth-url`);
      // Pour Gmail, redirection top-level (le callback redirige vers /app/integrations?gmail=connected)
      if (provider === "gmail") {
        window.location.href = data.auth_url;
        return;
      }
      window.open(data.auth_url, "_blank", "width=800,height=700");
      toast.success("Une fenêtre s'est ouverte pour autoriser la connexion");
      setTimeout(load, 5000);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Erreur";
      toast.error(detail);
    } finally { setBusy(""); }
  };

  const disconnect = async (provider) => {
    // TikTok se gère sur sa page dédiée (stockage tiktok_accounts)
    if (provider === "tiktok") {
      window.location.href = "/app/tiktok";
      return;
    }
    if (!confirm(`Déconnecter ${PROVIDER_META[provider].label} ?`)) return;
    await api.delete(`/integrations/${provider}`);
    load();
    toast.success("Déconnecté");
  };

  const grouped = CATEGORIES.map(cat => ({
    ...cat,
    items: list.filter(i => PROVIDER_META[i.provider]?.category === cat.k),
  }));

  return (
    <div data-testid="integrations-page">
      <div className="px-8 lg:px-12 pt-8">
        <PageHeader
          title="Intégrations"
          icon={Plug}
          breadcrumbs={[{ label: "IA, système & support" }, { label: "Intégrations" }]}
          subtitle="Branche tes vrais comptes pros pour activer la publication automatique, la sync comptable et la génération vidéo."
        />
      </div>

      <div className="px-8 lg:px-12 py-8 space-y-10">
        <div className="hard-border p-5 bg-secondary/30 flex items-start gap-3">
          <Lock size={20} className="text-[#D90429] mt-0.5"/>
          <div className="text-sm">
            <strong>Sécurité.</strong> Tes credentials d'API sont stockées en variables d'environnement côté serveur (jamais exposées au navigateur). Seul TON compte voit TES connexions. Tu peux déconnecter à tout moment.
          </div>
        </div>

        {grouped.map(cat => (
          <section key={cat.k}>
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <div className="ticker text-muted-foreground">— {cat.label}</div>
                <div className="text-sm text-muted-foreground">{cat.desc}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 hard-border">
              {cat.items.map((i, idx) => {
                const meta = PROVIDER_META[i.provider];
                const Icon = meta.icon;
                const status = i.status;
                const connected = status === "connected";
                const configured = i.has_credentials;
                return (
                  <div key={i.provider} data-testid={`integ-${i.provider}`} className={`p-6 ${idx % 2 === 0 ? "md:border-r border-border" : ""} ${idx < cat.items.length - (cat.items.length % 2 === 0 ? 2 : 1) ? "border-b border-border" : ""}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 hard-border flex items-center justify-center flex-shrink-0" style={{ background: meta.color + "12", borderColor: meta.color + "40" }}>
                        <Icon size={24} weight="duotone" style={{ color: meta.color }}/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-display font-bold">{meta.label}</div>
                          {connected && <span className="ticker bg-[#2A9D8F] text-white px-2 py-0.5 flex items-center gap-1"><CheckCircle size={10} weight="fill"/> connecté</span>}
                          {status === "expired" && <span className="ticker bg-[#D90429] text-white px-2 py-0.5 flex items-center gap-1">⚠️ expiré · à reconnecter</span>}
                          {!configured && <span className="ticker bg-secondary px-2 py-0.5 text-muted-foreground">en attente clés</span>}
                          {configured && !connected && status !== "expired" && <span className="ticker bg-[#E9C46A] px-2 py-0.5">prêt à connecter</span>}
                        </div>
                        {connected && i.account_label && <div className="ticker text-muted-foreground mt-1">{i.account_label}</div>}
                        {!connected && <div className="text-xs text-muted-foreground mt-2">{meta.instructions}</div>}
                      </div>
                    </div>
                    <div className="mt-4 flex gap-2">
                      {!configured && (
                        <div className="ticker text-muted-foreground italic">Ajoute la clé dans backend/.env</div>
                      )}
                      {configured && !connected && status !== "expired" && i.auth_url_available && (
                        <button onClick={() => connect(i.provider)} disabled={busy === i.provider} data-testid={`connect-${i.provider}`}
                          className="bg-[#D90429] hover:bg-[#BF0424] text-white px-4 py-2 ticker flex items-center gap-2 transition-colors disabled:opacity-60">
                          <Plug size={14}/> {busy === i.provider ? "Ouverture…" : "Connecter"} <ArrowRight size={12}/>
                        </button>
                      )}
                      {status === "expired" && i.auth_url_available && (
                        <button onClick={() => connect(i.provider)} disabled={busy === i.provider} data-testid={`reconnect-${i.provider}`}
                          className="bg-[#D90429] hover:bg-[#BF0424] text-white px-4 py-2 ticker flex items-center gap-2 transition-colors disabled:opacity-60">
                          <Plug size={14}/> {busy === i.provider ? "Ouverture…" : "RECONNECTER"} <ArrowRight size={12}/>
                        </button>
                      )}
                      {configured && !connected && status !== "expired" && !i.auth_url_available && (
                        <div className="ticker text-muted-foreground italic">Clé API présente · sera utilisée automatiquement par les modules concernés</div>
                      )}
                      {connected && (
                        <button onClick={() => disconnect(i.provider)} data-testid={`disconnect-${i.provider}`}
                          className="hard-border px-4 py-2 ticker hover:bg-[#D90429] hover:text-white hover:border-[#D90429] flex items-center gap-2 transition-colors">
                          <X size={14}/> Déconnecter
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        <div className="hard-border p-6 bg-secondary/20 flex items-start gap-3">
          <Lightning size={20} className="text-[#D90429] mt-0.5"/>
          <div className="text-sm">
            <strong>Comment ajouter tes clés ?</strong> Donne-les-moi (Ralph) dans la conversation. Je les ajoute aux variables d'environnement du serveur, je redémarre, et la connexion devient disponible. Tu cliques ensuite « Connecter » pour autoriser ton compte (OAuth) — tu retrouves ta connexion ici dès que c'est fait.
          </div>
        </div>
      </div>
    </div>
  );
}
